import { Server as HttpServer } from "node:http";
import { Server as HttpsServer } from "node:https";

import { createAdapter } from "@socket.io/redis-adapter";
import mongoose from "mongoose";
import { Server as SocketIOServer, Socket } from "socket.io";

import { Chat, Message, MESSAGE_STATUS } from "../models";
import { redisService } from "../services/redis-service";
import { JWTPayload, verifyAccessToken } from "../utils/jwt-utils";
import { logger } from "../utils/logger";

/**
 * Socket.io Server for real-time chat
 * Handles WebSocket connections, message delivery, typing indicators, and online status
 */
class SocketServer {
  private io: SocketIOServer | null = null;
  private connectedUsers = new Map<
    string,
    { socketId: string; role: string }
  >();

  /**
   * Initialize Socket.io server
   */
  initialize(httpServer: HttpServer | HttpsServer): void {
    const corsOptions = {
      origin: process.env.CORS_ORIGIN?.split(",") || "*",
      methods: ["GET", "POST"],
      credentials: true,
    };

    this.io = new SocketIOServer(httpServer, {
      cors: corsOptions,
      transports: ["websocket", "polling"],
      pingTimeout: 60_000,
      pingInterval: 25_000,
    });

    // Use Redis adapter for horizontal scaling
    if (redisService.isReady()) {
      try {
        const { pubClient, subClient } = redisService.getClients();
        this.io.adapter(createAdapter(pubClient, subClient));
        logger.info("Socket.io Redis adapter initialized");
      } catch (error) {
        logger.error("Failed to initialize Redis adapter:", error);
      }
    }

    // Authentication middleware
    this.io.use(async (socket, next) => {
      try {
        const token =
          socket.handshake.auth.token ||
          socket.handshake.headers.authorization?.replace("Bearer ", "");

        if (!token) {
          next(new Error("Authentication token required"));
          return;
        }

        const decoded = verifyAccessToken(token);

        // Attach user info to socket
        (socket as any).userId = decoded.userId;
        (socket as any).role = decoded.role;
        // schoolId might not be in JWT payload, will be fetched from user profile if needed

        next();
      } catch (error) {
        logger.error("Socket authentication error:", error);
        next(new Error("Authentication failed"));
      }
    });

    // Connection handler
    this.io.on("connection", (socket: Socket) => {
      this.handleConnection(socket);
    });

    logger.info("Socket.io server initialized");
  }

  /**
   * Handle new socket connection
   */
  private handleConnection(socket: Socket): void {
    const userId = (socket as any).userId as string;
    const role = (socket as any).role as string;
    const schoolId = (socket as any).schoolId as string | undefined;

    // Store user connection
    this.connectedUsers.set(userId, { socketId: socket.id, role });

    // Join user-specific room
    void socket.join(`user:${userId}`);

    // Join role-specific room
    void socket.join(`role:${role}`);

    // Join school room if applicable
    if (schoolId) {
      void socket.join(`school:${schoolId}`);
    }

    // Set user online in Redis
    void redisService.setUserOnline(userId, role).catch((error: unknown) => {
      logger.error("Error setting user online:", error);
    });

    // Emit online status to relevant users
    this.emitUserOnlineStatus(userId, role, true);

    logger.info(`User connected: ${userId} (${role}) - Socket: ${socket.id}`);

    // Handle disconnect
    socket.on("disconnect", async () => {
      await this.handleDisconnect(socket, userId, role);
    });

    // Handle join chat room
    socket.on("join_chat", async (data: { chatId: string }) => {
      await this.handleJoinChat(socket, data.chatId, userId, role);
    });

    // Handle leave chat room
    socket.on("leave_chat", (data: { chatId: string }) => {
      void socket.leave(`chat:${data.chatId}`);
    });

    // Handle send message
    socket.on(
      "send_message",
      async (data: {
        chatId: string;
        content: string;
        type?: string;
        attachments?: any[];
        replyTo?: string;
      }) => {
        await this.handleSendMessage(socket, data, userId, role);
      },
    );

    // Handle typing indicator
    socket.on("typing", (data: { chatId: string; isTyping: boolean }) => {
      this.handleTyping(socket, data, userId, role);
    });

    // Handle stop typing
    socket.on("stop_typing", (data: { chatId: string }) => {
      this.handleStopTyping(socket, data, userId, role);
    });

    // Handle mark message as read
    socket.on(
      "mark_read",
      async (data: { chatId: string; messageId?: string }) => {
        await this.handleMarkRead(socket, data, userId, role);
      },
    );

    // Handle mark message as delivered
    socket.on("mark_delivered", async (data: { messageId: string }) => {
      await this.handleMarkDelivered(socket, data, userId);
    });

    // Handle get online status
    socket.on("get_online_status", async (data: { userId: string }) => {
      await this.handleGetOnlineStatus(socket, data.userId);
    });
  }

  /**
   * Handle disconnect
   */
  private async handleDisconnect(
    socket: Socket,
    userId: string,
    role: string,
  ): Promise<void> {
    this.connectedUsers.delete(userId);

    // Set user offline in Redis
    await redisService.setUserOffline(userId, role);

    // Emit offline status
    this.emitUserOnlineStatus(userId, role, false);

    logger.info(`User disconnected: ${userId} (${role})`);
  }

  /**
   * Handle join chat room
   */
  private async handleJoinChat(
    socket: Socket,
    chatId: string,
    userId: string,
    role: string,
  ): Promise<void> {
    try {
      // Verify user has access to this chat
      const chat = await Chat.findById(chatId);
      if (!chat) {
        socket.emit("error", { message: "Chat not found" });
        return;
      }

      // Check if user is participant
      const isParticipant = chat.participants.some(
        p => p.toString() === userId,
      );

      if (!isParticipant) {
        socket.emit("error", { message: "Access denied to this chat" });
        return;
      }

      // Join chat room
      void socket.join(`chat:${chatId}`);

      // Mark chat as read for this user
      await chat.markAsRead(role as "school" | "student" | "teacher");

      // Mark all messages in chat as delivered
      // eslint-disable-next-line node/no-unsupported-features/es-syntax
      const mongooseModule = await import("mongoose");
      await Message.updateMany(
        {
          chatId: new mongooseModule.Types.ObjectId(chatId),
          status: { $ne: MESSAGE_STATUS.READ },
          senderId: {
            $ne: new mongooseModule.Types.ObjectId(userId),
          },
        },
        { $set: { status: MESSAGE_STATUS.DELIVERED } },
      );

      socket.emit("joined_chat", { chatId });
    } catch (error) {
      logger.error("Error joining chat:", error);
      socket.emit("error", { message: "Failed to join chat" });
    }
  }

  /**
   * Handle send message
   */
  private async handleSendMessage(
    socket: Socket,
    data: {
      chatId: string;
      content: string;
      type?: string;
      attachments?: any[];
      replyTo?: string;
    },
    userId: string,
    role: string,
  ): Promise<void> {
    try {
      // eslint-disable-next-line node/no-unsupported-features/es-syntax
      const mongooseModule = await import("mongoose");

      // Verify chat exists and user has access
      const chat = await Chat.findById(data.chatId);
      if (!chat) {
        socket.emit("error", { message: "Chat not found" });
        return;
      }

      // Create message
      const message = new Message({
        chatId: new mongooseModule.Types.ObjectId(data.chatId),
        senderId: new mongooseModule.Types.ObjectId(userId),
        senderRole: role as "school" | "student" | "teacher",
        type: data.type || "text",
        content: data.content,
        attachments: data.attachments || [],
        status: MESSAGE_STATUS.SENT,
        replyTo: data.replyTo
          ? new mongooseModule.Types.ObjectId(data.replyTo)
          : undefined,
      });

      await message.save();

      // Update chat last message
      chat.lastMessage = message._id;
      chat.lastMessageAt = new Date();
      await chat.incrementUnread(role as "school" | "student" | "teacher");
      await chat.save();

      // Populate message for response
      const populatedMessage = await Message.findById(message._id)
        .populate("sender", "fullName email profilePicture")
        .populate("replyTo", "content senderId");

      // Emit to all users in chat room
      if (!this.io) return;
      this.io.to(`chat:${data.chatId}`).emit("new_message", {
        message: {
          id: message.id,
          chatId: message.chatId,
          senderId: message.senderId,
          senderRole: message.senderRole,
          type: message.type,
          content: message.content,
          attachments: message.attachments,
          status: message.status,
          replyTo: populatedMessage?.replyTo,
          createdAt: message.createdAt,
          sender: (populatedMessage as any)?.sender,
        },
      });

      // Emit message sent confirmation to sender
      socket.emit("message_sent", {
        messageId: message.id,
        chatId: data.chatId,
      });

      // Mark as delivered if recipient is online
      const recipients = chat.participants.filter(p => p.toString() !== userId);

      for (const recipient of recipients) {
        const recipientId = recipient.toString();
        const isOnline = await redisService.isUserOnline(recipientId);

        if (isOnline) {
          // Check if recipient is in chat room
          if (!this.io) return;
          const recipientSockets = await this.io
            .in(`chat:${data.chatId}`)
            .fetchSockets();
          const isInChat = recipientSockets.some(
            s => (s as any).userId === recipientId,
          );

          if (isInChat) {
            // Mark as delivered immediately
            message.status = MESSAGE_STATUS.DELIVERED;
            await message.save();

            // Emit delivered status
            if (!this.io) return;
            this.io.to(`user:${recipientId}`).emit("message_delivered", {
              messageId: message.id,
              chatId: data.chatId,
            });
          }
        }
      }

      // Cache message
      await redisService.cacheMessage(data.chatId, message.id, {
        id: message.id,
        content: message.content,
        senderId: message.senderId,
        createdAt: message.createdAt,
      });

      // Invalidate chat cache for participants
      for (const participant of chat.participants) {
        const participantId = participant.toString();
        const participantRole = this.getUserRole(participantId);
        if (participantRole) {
          await redisService.invalidateUserChats(
            participantId,
            participantRole,
          );
        }
      }
    } catch (error) {
      logger.error("Error sending message:", error);
      socket.emit("error", { message: "Failed to send message" });
    }
  }

  /**
   * Handle typing indicator
   */
  private handleTyping(
    socket: Socket,
    data: { chatId: string; isTyping: boolean },
    userId: string,
    role: string,
  ): void {
    socket.to(`chat:${data.chatId}`).emit("typing", {
      chatId: data.chatId,
      userId,
      role,
      isTyping: data.isTyping,
    });
  }

  /**
   * Handle stop typing
   */
  private handleStopTyping(
    socket: Socket,
    data: { chatId: string },
    userId: string,
    role: string,
  ): void {
    socket.to(`chat:${data.chatId}`).emit("stop_typing", {
      chatId: data.chatId,
      userId,
      role,
    });
  }

  /**
   * Handle mark message as read
   */
  private async handleMarkRead(
    socket: Socket,
    data: { chatId: string; messageId?: string },
    userId: string,
    role: string,
  ): Promise<void> {
    try {
      if (data.messageId) {
        // Mark specific message as read
        const message = await Message.findById(data.messageId);
        if (message) {
          const messageDoc = message as any;
          if (messageDoc.markAsRead) {
            // eslint-disable-next-line node/no-unsupported-features/es-syntax
            const mongooseModule = await import("mongoose");
            await messageDoc.markAsRead(
              new mongooseModule.Types.ObjectId(userId),
            );
          } else {
            // Fallback: manual update
            if (!messageDoc.readBy) {
              messageDoc.readBy = [];
            }
            const existingRead = messageDoc.readBy.find(
              (read: any) => read.userId.toString() === userId,
            );
            if (!existingRead) {
              // eslint-disable-next-line node/no-unsupported-features/es-syntax
              const mongooseModule = await import("mongoose");
              messageDoc.readBy.push({
                userId: new mongooseModule.Types.ObjectId(userId),
                readAt: new Date(),
              });
              messageDoc.status = MESSAGE_STATUS.READ;
              await messageDoc.save();
            }
          }
        }
      } else {
        // Mark all messages in chat as read
        // eslint-disable-next-line node/no-unsupported-features/es-syntax
        const mongooseModule = await import("mongoose");
        await Message.updateMany(
          {
            chatId: new mongooseModule.Types.ObjectId(data.chatId),
            senderId: { $ne: new mongooseModule.Types.ObjectId(userId) },
            status: { $ne: MESSAGE_STATUS.READ },
          },
          {
            $push: {
              readBy: {
                userId: new mongooseModule.Types.ObjectId(userId),
                readAt: new Date(),
              },
            },
            $set: { status: MESSAGE_STATUS.READ },
          },
        );
      }

      // Update chat unread count
      const chat = await Chat.findById(data.chatId);
      if (chat) {
        await chat.markAsRead(role as "school" | "student" | "teacher");
      }

      // Emit read receipt
      socket.to(`chat:${data.chatId}`).emit("message_read", {
        chatId: data.chatId,
        messageId: data.messageId,
        userId,
        role,
      });
    } catch (error) {
      logger.error("Error marking message as read:", error);
      socket.emit("error", { message: "Failed to mark message as read" });
    }
  }

  /**
   * Handle mark message as delivered
   */
  private async handleMarkDelivered(
    socket: Socket,
    data: { messageId: string },
    userId: string,
  ): Promise<void> {
    try {
      const message = await Message.findById(data.messageId);
      if (message && message.senderId.toString() !== userId) {
        const messageDoc = message as any;
        if (messageDoc.markAsDelivered) {
          await messageDoc.markAsDelivered();
        } else {
          // Fallback: manual update
          if (messageDoc.status === MESSAGE_STATUS.SENT) {
            messageDoc.status = MESSAGE_STATUS.DELIVERED;
            await messageDoc.save();
          }
        }

        // Emit delivered status to sender
        if (!this.io) return;
        this.io
          .to(`user:${message.senderId.toString()}`)
          .emit("message_delivered", {
            messageId: message.id,
            chatId: message.chatId.toString(),
          });
      }
    } catch (error) {
      logger.error("Error marking message as delivered:", error);
    }
  }

  /**
   * Handle get online status
   */
  private async handleGetOnlineStatus(
    socket: Socket,
    userId: string,
  ): Promise<void> {
    try {
      const status = await redisService.getUserOnlineStatus(userId);
      socket.emit("online_status", {
        userId,
        ...status,
      });
    } catch (error) {
      logger.error("Error getting online status:", error);
    }
  }

  /**
   * Emit user online status
   */
  private emitUserOnlineStatus(
    userId: string,
    role: string,
    isOnline: boolean,
  ): void {
    // Emit to school if user belongs to a school
    // This would need schoolId from user profile
    if (!this.io) return;
    this.io.to(`role:${role}`).emit("user_status_changed", {
      userId,
      role,
      isOnline,
    });
  }

  /**
   * Get user role from connected users
   */
  private getUserRole(userId: string): string | null {
    const user = this.connectedUsers.get(userId);
    return user?.role || null;
  }

  /**
   * Get Socket.io server instance
   */
  getIO(): SocketIOServer {
    if (!this.io) {
      throw new Error("Socket.io server not initialized");
    }
    return this.io;
  }

  /**
   * Emit event to specific user
   */
  emitToUser(userId: string, event: string, data: any): void {
    if (!this.io) return;
    this.io.to(`user:${userId}`).emit(event, data);
  }

  /**
   * Emit event to chat room
   */
  emitToChat(chatId: string, event: string, data: any): void {
    if (!this.io) return;
    this.io.to(`chat:${chatId}`).emit(event, data);
  }
}

export const socketServer = new SocketServer();
